import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import DeleteIcon from '../../svgs/DeleteIcon';
import MoreVertIcon from '../../svgs/MoreVertIcon';
import RotatingSquare from '../../svgs/RotatingSquare';
import TimelinePhotoIcon from '../../svgs/TimelinePhotoIcon';
import applyImageOptimizations from '../../utility/applyImageOptimizations';
import SpinningLoading from '../SpinningLoading';
import AddIconWrapper from './AddIconWrapper';
import AutoResizeableMultilineInput from './AutoResizeableMultilineInput';
import {
  ContextMenuItem,
  ContextMenuSurface,
  KebabButton,
} from './contextMenuStyles';
import EnlargedImageRenderProp from './EnlargedImageRenderProp';
import { useAnchoredDropdown } from './useAnchoredDropdown';

import type { Artifact, Showcase } from '../../types.ts/story';

type Props = {
  item: {
    id: string;
    src: string;
    title?: string;
    description: string;
    thumbnailUrl?: string;
    aspectRatio?: number;
    alt?: string;
    slug?: string;
    type?: 'artifact' | 'stock' | 'ai';
    attribution?: any;
  };
  type:
    | 'storyArtifacts'
    | 'storyArtifactsVideo'
    | 'organizationArtifacts'
    | 'organizationArtifactsVideo'
    | 'organizationLogos';
  isSelected: boolean;
  handleClick?: () => void; // Make optional
  handleRemove: () => void;
  handleRegenerateTitle?: () => Promise<void>;
  callback?: (artifact?: Artifact[], showcases?: Showcase[]) => void;
  // Additional props for enhanced media handling
  mediaOptions?: {
    tab?: any; // PhotoArtifactTab
    searchValue?: string;
    isLogo?: boolean;
  };
};

const SMALL_PHOTO_HEIGHT = 70;
/** Wide enough for "Regenerate Description" on one line. */
const MENU_WIDTH_PX = 220;

const FileListItem = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();

  const {
    item,
    type,
    isSelected = false,
    callback,
    handleClick,
    mediaOptions,
  } = props;
  const [isActionClicked, setIsActionClicked] = useState(false);
  const [description, setDescription] = useState(item.description);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  // The list scrolls (ArtifactsAndAssets' Main is overflow-y: auto), which
  // clips an absolutely-positioned menu no matter its z-index — so portal it to
  // the body and anchor it to the kebab. The hook also owns dismissal.
  // `auto` placement: a row near the viewport bottom would otherwise get a menu
  // squeezed into the few pixels below its kebab (the design system's
  // bottom-edge flip for context menus).
  const { menuRef, style: menuStyle } = useAnchoredDropdown(
    isActionClicked,
    menuTriggerRef,
    () => setIsActionClicked(false),
    undefined,
    { width: MENU_WIDTH_PX, align: 'end', placement: 'auto' },
  );

  const handleUpdateDescription = async () => {
    if (item.description === description) return;

    switch (type) {
      case 'storyArtifacts':
        videoCreator.story!.storyArtifacts =
          videoCreator.story?.storyArtifacts?.map(f =>
            f.id === item.id ? { ...f, title: description } : f,
          );
        callback && callback(videoCreator.story!.storyArtifacts!);
        await videoCreator.updateStory(videoCreator.story!);
        break;
      case 'storyArtifactsVideo':
        videoCreator.story!.storyArtifactsVideo =
          videoCreator.story?.storyArtifactsVideo?.map(f =>
            f.id === item.id ? { ...f, title: description } : f,
          );
        props.callback &&
          props.callback(videoCreator.story!.storyArtifactsVideo!);
        await videoCreator.updateStory(videoCreator.story!);
        break;
      case 'organizationArtifacts': {
        const idx = videoCreator.story?._allReferencingShowcases?.findIndex(s =>
          s.organizationArtifacts?.some(a => a.id === item.id),
        );
        if (idx !== undefined && idx > -1) {
          videoCreator.story!._allReferencingShowcases[
            idx
          ].organizationArtifacts =
            videoCreator.story!._allReferencingShowcases[
              idx
            ].organizationArtifacts?.map(f =>
              f.id === item.id ? { ...f, title: description } : f,
            );
          props.callback &&
            props.callback(
              undefined,
              videoCreator.story?._allReferencingShowcases,
            );
          await datoClientStore.albumRepository?.update(
            videoCreator.story!._allReferencingShowcases[idx],
          );
        }
        break;
      }
      case 'organizationLogos': {
        const idx = videoCreator.story?._allReferencingShowcases?.findIndex(s =>
          s.organizationLogos?.some(a => a.id === item.id),
        );
        if (idx !== undefined && idx > -1) {
          videoCreator.story!._allReferencingShowcases[idx].organizationLogos =
            videoCreator.story!._allReferencingShowcases[
              idx
            ].organizationLogos?.map(f =>
              f.id === item.id ? { ...f, title: description } : f,
            );
          props.callback &&
            props.callback(
              undefined,
              videoCreator.story?._allReferencingShowcases,
            );
          await datoClientStore.albumRepository?.update(
            videoCreator.story!._allReferencingShowcases[idx],
          );
        }
        break;
      }
      case 'organizationArtifactsVideo': {
        const index = videoCreator.story?._allReferencingShowcases?.findIndex(
          s => s.organizationArtifactsVideo?.some(a => a.id === item.id),
        );
        if (index !== undefined && index > -1) {
          videoCreator.story!._allReferencingShowcases[
            index
          ].organizationArtifactsVideo =
            videoCreator.story!._allReferencingShowcases[
              index
            ].organizationArtifactsVideo?.map(f =>
              f.id === item.id ? { ...f, title: description } : f,
            );
          await datoClientStore.albumRepository?.update(
            videoCreator.story!._allReferencingShowcases[index],
          );
        }
        break;
      }
    }

    await datoClientStore.assetRepository?.update(item.id, {
      title: description,
      alt: description,
    });
  };

  const isMediaType =
    type === 'storyArtifacts' ||
    type === 'organizationArtifacts' ||
    type === 'organizationLogos' ||
    type === 'storyArtifactsVideo' ||
    type === 'organizationArtifactsVideo';

  return (
    <Entry
      ref={containerRef}
      key={item.id}
      onClick={handleClick}
      isSelected={isSelected}
    >
      <EnlargedImageRenderProp
        src={
          item.thumbnailUrl ||
          (type === 'storyArtifactsVideo' ||
          type === 'organizationArtifactsVideo'
            ? item.src
            : applyImageOptimizations(item.src, {
                height: 337,
              }))
        }
        smallPhotoHeight={SMALL_PHOTO_HEIGHT}
        isVideo={
          !item.thumbnailUrl &&
          (type === 'storyArtifactsVideo' ||
            type === 'organizationArtifactsVideo')
        }
        isLogo={type === 'organizationLogos' || mediaOptions?.isLogo}
      >
        {({ enlargedState, setEnlargedState, renderEnlargedImage }) => (
          <>
            {isMediaType ? (
              <AddIconWrapper
                onClick={() => handleClick && handleClick()}
                hoverLabel="Add"
                customStyles={{
                  borderRadius: '6px',
                  border: '2px solid transparent',
                  transition: 'border-color 0.15s ease',
                }}
                customStylesWhenHovered={{
                  borderColor: 'var(--primary)',
                }}
                onMouseEnter={e => {
                  setEnlargedState(e.currentTarget.getBoundingClientRect());
                }}
                onMouseMove={e => {
                  if (!enlargedState) {
                    setEnlargedState(e.currentTarget.getBoundingClientRect());
                  }
                }}
                onMouseLeave={() => {
                  setEnlargedState(null);
                }}
              >
                <Wrapper className="target" hasImage={!!item.src}>
                  {item.src ? (
                    <>
                      {item.thumbnailUrl ? (
                        <Image src={item.thumbnailUrl} />
                      ) : type === 'storyArtifactsVideo' ||
                        type === 'organizationArtifactsVideo' ? (
                        <Video src={item.src} controls={false} />
                      ) : isMediaType ? (
                        <Image
                          src={applyImageOptimizations(item.src, {
                            width: 100,
                          })}
                        />
                      ) : (
                        <Video src={item.src} />
                      )}
                    </>
                  ) : (
                    <TimelinePhotoIcon strokeColor="var(--text-muted)" />
                  )}
                </Wrapper>
              </AddIconWrapper>
            ) : (
              <Wrapper className="target" hasImage={!!item.src}>
                {item.src ? (
                  <Video src={item.src} />
                ) : (
                  <TimelinePhotoIcon strokeColor="var(--text-muted)" />
                )}
              </Wrapper>
            )}
            {enlargedState && !isActionClicked && renderEnlargedImage()}
          </>
        )}
      </EnlargedImageRenderProp>
      <Text>
        {item.title && <div className="quote">{item.title}</div>}
        <TextDescription>
          {/* <div className="text">{item.description}</div> */}
          <AutoResizeableMultilineInput
            customStyles={{
              color: 'var(--text-secondary)',
              // 10px / weight 300 were off the type scale in both directions:
              // --text-xs (12px) is the scale floor and 300 is lighter than any
              // list typography the doc defines. The row's 54px box still fits
              // three lines at this size, and these names wrap to two.
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-weight-normal)',
              lineHeight: 'var(--leading-text-xs)',
              paddingLeft: 'var(--spacing-xs)',
              paddingRight: 'var(--spacing-xs)',
              minHeight: '54px',
            }}
            hasOutline={false}
            hasBorder={false}
            borderOnFocus={true}
            placeholder="(Type to update description)"
            width="100%"
            customHeight="54px"
            value={item.description}
            maxResizeHeight={54}
            disableAutoResize={false}
            onBlur={handleUpdateDescription}
            getValue={description => {
              setDescription(description);
            }}
          />
          <MenuTrigger
            ref={menuTriggerRef}
            aria-label="Asset options"
            aria-haspopup="menu"
            aria-expanded={isActionClicked}
            onClick={e => {
              e.stopPropagation();
              setIsActionClicked(open => !open);
            }}
          >
            <MoreVertIcon width="14" height="14" />
          </MenuTrigger>
        </TextDescription>
      </Text>
      {isActionClicked &&
        menuStyle &&
        createPortal(
          <ActionsWrapper ref={menuRef} style={menuStyle}>
            {props.handleRegenerateTitle && (
              <ContextMenuItem
                onClick={async e => {
                  e.stopPropagation();
                  setIsUpdating(true);
                  setIsActionClicked(false);
                  try {
                    await props.handleRegenerateTitle!();
                  } finally {
                    setIsUpdating(false);
                  }
                }}
              >
                <RotatingSquare strokeColor="currentColor" />
                <span>Regenerate Description</span>
              </ContextMenuItem>
            )}
            <ContextMenuItem
              $danger
              onClick={async e => {
                e.stopPropagation();
                props.handleRemove();
                setIsActionClicked(false);
              }}
            >
              <DeleteIcon strokeColor="currentColor" />
              <span>Remove</span>
            </ContextMenuItem>
          </ActionsWrapper>,
          document.body,
        )}
      {isUpdating && (
        <SpinningLoading
          customStyle={{
            width: '100%',
            height: '100%',
          }}
          iconStyle={{ left: '11px', top: '11px' }}
          spinnerStyle={{ width: '46px', height: '46px' }}
          spinnerCircleStyle={{ borderWidth: '2px', boxSizing: 'border-box' }}
          textStyle={{ display: 'none' }}
        />
      )}
    </Entry>
  );
});

export default FileListItem;

export const Entry = styled.div<{ isSelected: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: 0 auto;
  cursor: pointer;
  border-radius: 8px;
  height: ${SMALL_PHOTO_HEIGHT}px;
  position: relative;

  ${props =>
    props.isSelected &&
    css`
      border-radius: 8px;
    `}
`;
const Text = styled.div`
  width: 65%;
  padding-right: 5px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1px;
  height: 59px;
  /* Entry is a fixed 70px and this column is 59px, but the description input
     auto-resizes up to 70px — so a long name overflowed both and, with no
     clipping anywhere in the chain, painted outside the row. Clip here rather
     than on Entry, which hosts the absolutely-positioned overflow menu and would
     swallow it. */
  overflow: hidden;
  min-width: 0;

  .quote {
    width: 100%;
    white-space: nowrap;
    color: var(--foreground);
    font-weight: var(--font-weight-normal);
    line-height: 19.2px;
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: var(--text-xs);
  }
`;

const TextDescription = styled.div`
  display: flex;
  gap: 3px;
  .text {
    color: var(--text-muted);
    font-size: 10px;
    font-weight: 300;
    line-height: 12px;
    width: 90%;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-height: 38px;
    overflow-y: hidden;
  }
`;
export const Wrapper = styled.div<{ hasImage: boolean }>`
  position: relative;
  color: var(--gray-400);
  width: 100px;
  height: ${SMALL_PHOTO_HEIGHT}px;
  /* Logos come through with fit=contain and varying aspect ratios; clip so none
     of them can paint outside the thumbnail box. */
  overflow: hidden;
  flex-shrink: 0;
`;
const Image = styled.img`
  width: 100%;
  height: 100%;
  /* Contain, not cover: an asset wider or taller than the box (a banner logo
     especially) has to fit whole rather than be cropped to a middle slice. */
  object-fit: contain;
  border-radius: 4px;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
`;

/**
 * The shared kebab trigger. Was a bare div with layout only — no hover, no focus,
 * no button semantics — holding a horizontal "..." where the standard is the
 * vertical three-dot kebab.
 */
const MenuTrigger = styled(KebabButton)`
  margin-left: auto;
  flex-shrink: 0;
  /* TextDescription is a flex row with the default align-items: stretch, so the
     button grew to the full row height and its hover background rendered as a
     tall vertical pill. Centre it and give it square dimensions so the hover is
     a small rounded square around the glyph. */
  align-self: center;
  width: 28px;
  height: 28px;
  padding: 0;
`;

const ActionsWrapper = styled(ContextMenuSurface)``;
